import { For, Show, createSignal, onMount } from "solid-js";
import { formatDate, type AdminApi, type Paged, type TeacherAction, type TeacherRow } from "./api";

const ACTIONS: Record<TeacherAction, { label: string; confirm: string; danger: boolean }> = {
  suspend: { label: "Sperren", confirm: "Lehrkraft sperren", danger: true },
  reactivate: { label: "Entsperren", confirm: "Lehrkraft entsperren", danger: false },
  "revoke-sessions": { label: "Sitzungen beenden", confirm: "Sitzungen beenden", danger: false }
};

export function TeachersView(props: { api: AdminApi; guard: <T>(work: () => Promise<T>) => Promise<T> }) {
  const [data, setData] = createSignal<Paged<TeacherRow>>({ items: [], total: 0, page: 1, limit: 25 });
  const [search, setSearch] = createSignal("");
  const [loading, setLoading] = createSignal(true);
  const [error, setError] = createSignal("");
  const [pending, setPending] = createSignal<{ teacher: TeacherRow; action: TeacherAction } | null>(null);
  const [dialogError, setDialogError] = createSignal("");
  const [saving, setSaving] = createSignal(false);
  let reason!: HTMLTextAreaElement;

  const load = async (page = 1) => {
    setLoading(true);
    setError("");
    try {
      setData(await props.guard(() => props.api.teachers(page, search())));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Laden fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };
  onMount(() => void load());

  const confirm = async (event: SubmitEvent) => {
    event.preventDefault();
    const current = pending();
    if (!current) return;
    setSaving(true);
    setDialogError("");
    try {
      await props.guard(() => props.api.teacherAction(current.teacher.id, current.action, reason.value.trim()));
      setPending(null);
      await load(data().page);
    } catch (cause) {
      setDialogError(cause instanceof Error ? cause.message : "Aktion fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const pages = () => Math.max(1, Math.ceil(data().total / data().limit));

  return (
    <section>
      <h2>Lehrkräfte</h2>
      <form class="adm-bar" onSubmit={(event) => { event.preventDefault(); void load(1); }}>
        <input class="adm-input" type="search" placeholder="Suchen nach Name, E-Mail oder Schule" aria-label="Lehrkräfte suchen" value={search()} onInput={(event) => setSearch(event.currentTarget.value)} />
        <button class="adm-btn ghost" type="submit">Suchen</button>
      </form>

      <Show when={pending()}>
        {(current) => (
          <form class="adm-card adm-dialog" onSubmit={confirm}>
            <h3>{ACTIONS[current().action].confirm}: {current().teacher.firstName} {current().teacher.lastName}</h3>
            <p class="adm-sub">{current().teacher.email}. Der Grund wird im Protokoll gespeichert.</p>
            <label class="adm-field">
              <span>Grund (mindestens 5 Zeichen)</span>
              <textarea ref={reason} class="adm-input" required minlength={5} maxlength={500} autofocus />
            </label>
            <div class="adm-bar">
              <button class={`adm-btn ${ACTIONS[current().action].danger ? "danger" : ""}`} type="submit" disabled={saving()}>{ACTIONS[current().action].label}</button>
              <button class="adm-btn ghost" type="button" onClick={() => setPending(null)}>Abbrechen</button>
            </div>
            <Show when={dialogError()}><p class="adm-error" role="alert">{dialogError()}</p></Show>
          </form>
        )}
      </Show>

      <Show when={error()}><p class="adm-error" role="alert">{error()}</p></Show>
      <div class="adm-card">
        <div class="adm-table-wrap">
          <table class="adm-table">
            <thead><tr><th>Name</th><th>E-Mail</th><th>Schule</th><th>Klassen</th><th>Schüler</th><th>Letzter Login</th><th>Status</th><th>Aktionen</th></tr></thead>
            <tbody>
              <For each={data().items}>
                {(teacher) => (
                  <tr>
                    <td>{teacher.firstName} {teacher.lastName}</td>
                    <td>{teacher.email}</td>
                    <td>{teacher.schoolName ?? "–"}</td>
                    <td class="num">{teacher.classCount}</td>
                    <td class="num">{teacher.studentCount}</td>
                    <td>{formatDate(teacher.lastLoginAt)}</td>
                    <td><span class={`adm-pill ${teacher.status}`}>{teacher.status === "active" ? "aktiv" : "gesperrt"}</span></td>
                    <td>
                      <div class="adm-actions">
                        <Show when={teacher.status === "active"} fallback={<button class="adm-btn ghost small" type="button" onClick={() => { setPending({ teacher, action: "reactivate" }); setDialogError(""); }}>Entsperren</button>}>
                          <button class="adm-btn ghost small" type="button" onClick={() => { setPending({ teacher, action: "suspend" }); setDialogError(""); }}>Sperren</button>
                        </Show>
                        <button class="adm-btn ghost small" type="button" onClick={() => { setPending({ teacher, action: "revoke-sessions" }); setDialogError(""); }}>Sitzungen beenden</button>
                      </div>
                    </td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </div>
        <Show when={!loading() && data().items.length === 0}><p class="adm-empty">Keine Lehrkräfte gefunden.</p></Show>
        <div class="adm-pager">
          <button class="adm-btn ghost small" type="button" disabled={data().page <= 1 || loading()} onClick={() => void load(data().page - 1)}>Zurück</button>
          <span>Seite {data().page} von {pages()} · {data().total} Lehrkräfte</span>
          <button class="adm-btn ghost small" type="button" disabled={data().page >= pages() || loading()} onClick={() => void load(data().page + 1)}>Weiter</button>
        </div>
      </div>
    </section>
  );
}
